import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Copy, Send, Image as ImageIcon, Eye, List } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitationsManager({ event }) {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [inviteList, setInviteList] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (event) {
      // Pre-fill default invitation message
      const defaultMessage = `Ciao!

Sei invitato all'evento:

🎉 ${event.title}
📅 ${new Date(event.start_date).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}
📍 ${event.location}

Acquista il tuo biglietto qui:
${window.location.origin}/api/events/${event.id}/public/share

Ci vediamo!`;
      
      setCustomMessage(defaultMessage);
    }
  }, [event]);

  const generateInviteLink = () => {
    // Use /share endpoint for better WhatsApp/social media preview
    return `${window.location.origin}/api/events/${event.id}/public/share`;
  };

  const copyInviteLink = () => {
    const link = generateInviteLink();
    navigator.clipboard.writeText(link);
    toast.success('Link copiato negli appunti!');
  };

  const sendInvitations = async () => {
    const emails = inviteList
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emails.length === 0) {
      toast.error('Inserisci almeno un indirizzo email');
      return;
    }

    setSending(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/events/${event.id}/invitations`,
        {
          emails: emails,
          message: customMessage || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.failed > 0) {
        toast.warning(
          `Inviti inviati a ${response.data.sent} destinatari. ${response.data.failed} non riusciti.`
        );
      } else {
        toast.success(`✅ Inviti inviati a ${response.data.sent} destinatari!`);
      }
      
      setInviteList('');
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error(error.response?.data?.detail || 'Errore invio inviti');
    } finally {
      setSending(false);
    }
  };

  const generateEmailHTML = () => {
    // HTML email template with logo
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invito - ${event.title}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f3f4f6; padding: 40px 0;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          
          <!-- Header with Logo -->
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center;">
              ${tenant?.logo_base64 ? `
                <img src="${tenant.logo_base64}" alt="${tenant.name}" style="max-width: 150px; height: auto; margin-bottom: 20px;">
              ` : ''}
              <h1 style="color: #ffffff; margin: 0; font-size: 28px;">Sei Invitato!</h1>
            </td>
          </tr>
          
          <!-- Content -->
          <tr>
            <td style="padding: 40px 30px;">
              <h2 style="color: #1f2937; font-size: 24px; margin-top: 0;">🎉 ${event.title}</h2>
              
              <table width="100%" cellpadding="10" cellspacing="0" style="margin: 30px 0; background-color: #f9fafb; border-radius: 8px;">
                <tr>
                  <td style="color: #4b5563; font-size: 16px;">
                    📅 <strong>Data:</strong><br>
                    ${new Date(event.start_date).toLocaleDateString('it-IT', {
                      weekday: 'long',
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                </tr>
                <tr>
                  <td style="color: #4b5563; font-size: 16px; border-top: 1px solid #e5e7eb; padding-top: 15px;">
                    📍 <strong>Luogo:</strong><br>
                    ${event.location}
                  </td>
                </tr>
              </table>
              
              ${event.description ? `
                <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 20px 0;">
                  ${event.description}
                </p>
              ` : ''}
              
              <!-- CTA Button -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="${generateInviteLink()}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 16px 40px; border-radius: 8px; font-size: 18px; font-weight: bold;">
                      🎟️ Acquista Biglietto
                    </a>
                  </td>
                </tr>
              </table>
              
              ${customMessage ? `
                <div style="margin-top: 30px; padding: 20px; background-color: #fef3c7; border-left: 4px solid #f59e0b; border-radius: 4px;">
                  <p style="color: #92400e; margin: 0; white-space: pre-wrap;">${customMessage}</p>
                </div>
              ` : ''}
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="background-color: #f9fafb; padding: 30px; text-align: center; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; font-size: 14px; margin: 0;">
                Organizzato da <strong>${tenant?.name || 'ZonePass'}</strong>
              </p>
              <p style="color: #9ca3af; font-size: 12px; margin: 10px 0 0 0;">
                Powered by ZonePass - Sistema di Ticketing Professionale
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `;
  };

  const previewEmail = () => {
    const html = generateEmailHTML();
    const newWindow = window.open();
    newWindow.document.write(html);
    newWindow.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Email Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Invito con Logo Personalizzato
          </CardTitle>
          <p className="text-sm text-gray-600">
            Gli inviti includeranno automaticamente il logo della tua organizzazione
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {tenant?.logo_base64 && (
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="text-sm text-gray-600 mb-2">Logo attuale:</p>
              <img 
                src={tenant.logo_base64} 
                alt={tenant.name}
                className="max-w-[200px] h-auto"
              />
            </div>
          )}

          <Button
            onClick={previewEmail}
            variant="outline"
            className="w-full gap-2"
          >
            <Eye className="w-4 h-4" />
            Anteprima Email Invito
          </Button>
        </CardContent>
      </Card>

      {/* Quick Share */}
      <Card>
        <CardHeader>
          <CardTitle>🔗 Condivisione Rapida</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Link Evento</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={generateInviteLink()}
                readOnly
                className="font-mono text-sm"
              />
              <Button onClick={copyInviteLink} variant="outline">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mass Email Invitations */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Invio Email Massivo
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/events/${event.id}/invitations`)}
              className="gap-2"
            >
              <List className="w-4 h-4" />
              Vedi Lista Invitati
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="inviteList">Lista Email</Label>
            <Textarea
              id="inviteList"
              value={inviteList}
              onChange={(e) => setInviteList(e.target.value)}
              placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
              rows={6}
              className="font-mono text-sm"
            />
            <p className="text-sm text-gray-500 mt-1">
              Inserisci un'email per riga. Puoi anche separarle con virgole o punto e virgola.
            </p>
          </div>

          <div>
            <Label htmlFor="customMessage">Messaggio Personalizzato (opzionale)</Label>
            <Textarea
              id="customMessage"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={8}
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Nota:</strong> Le email verranno inviate con il template brandizzato 
              che include il tuo logo. Configura IONOS SMTP nelle impostazioni per l'invio effettivo.
            </p>
          </div>

          <Button
            onClick={sendInvitations}
            disabled={sending || !inviteList.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700 gap-2"
          >
            {sending ? (
              'Invio in corso...'
            ) : (
              <>
                <Send className="w-4 h-4" />
                Invia Inviti
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
